import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import API from "./config/API";

const UpdateFloorData = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { floorId, roomId, totalBedsBooked = 0 } = location.state || {};

  // =========================================================
  // STATE
  // =========================================================

  const [floorData, setFloorData] = useState({
    floorId: "",
    floorName: "",
    roomId: "",
    roomName: "",
    roomActiveStatus: true,
    roomSharingType: "",
    totalBookedBeds: 0,
    isLockerAvailable: false,
    isSnoringRoom: false,
    createdBy: "",
    createdOn: "",
    updatedOn: "",
  });

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // =========================================================
  // GET LOGGED USER
  // =========================================================

  const getLoggedUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user;
    } catch (error) {
      console.error("Unable to read user from localStorage:", error);
      return null;
    }
  };

  // =========================================================
  // GET FLOOR + ROOM DETAILS
  // =========================================================

  useEffect(() => {
    console.log("=================================");
    console.log("UpdateFloorData Mounted");
    console.log("floorId:", floorId);
    console.log("roomId:", roomId);
    console.log("=================================");

    if (!floorId || !roomId) {
      setLoading(false);

      Swal.fire({
        icon: "error",
        title: "Missing Details",
        text: "Floor ID or Room ID is missing.",
      });

      return;
    }

    fetchFloorData();
  }, [floorId, roomId]);

  // =========================================================
  // FETCH FLOOR DATA
  // =========================================================

  const fetchFloorData = async () => {
    try {
      setLoading(true);

      const user = getLoggedUser();

      console.log("Logged User:", user);

      // =====================================================
      // TOKEN
      // =====================================================

      const token = user?.jwtToken;

      if (!token) {
        await Swal.fire({
          icon: "error",
          title: "Session Expired",
          text: "Login token not found. Please login again.",
          confirmButtonText: "Login",
        });

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
        return;
      }

      // =====================================================
      // DYNAMIC GET API
      // =====================================================

      const url = `${API.V1}/floor_rooms/getFloorById`;

      console.log("GET URL:", url);

      console.log("GET PARAMS:", {
        floorId,
        roomId,
      });

      const response = await axios.get(url, {
        params: {
          floorId: floorId,
          roomId: roomId,
        },

        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log("=================================");
      console.log("GET FLOOR ROOM RESPONSE");
      console.log(response.data);
      console.log("=================================");

      if (response.data?.statusCode === 200) {
        const data = response.data?.data;

        console.log("API DATA:", data);

        // ===================================================
        // BOOLEAN CONVERSION
        // ===================================================

        const lockerAvailable =
          data?.isLockerAvailable === true ||
          data?.isLockerAvailable === "true" ||
          data?.isLockerAvailable === 1 ||
          data?.isLockerAvailable === "1";

        const snoringRoom =
          data?.isSnoringRoom === true ||
          data?.isSnoringRoom === "true" ||
          data?.isSnoringRoom === 1 ||
          data?.isSnoringRoom === "1";

        // ===================================================
        // TOTAL BOOKED BEDS
        // ===================================================

        const bookedBeds = Number(
          data?.totalBookedBeds ??
            data?.totalBedsBooked ??
            totalBedsBooked ??
            0,
        );

        console.log("Locker Available:", lockerAvailable);

        console.log("Snoring Room:", snoringRoom);

        console.log("Total Beds Booked:", bookedBeds);

        // ===================================================
        // SET DATA
        // ===================================================

        setFloorData({
          floorId: data?.floorId ?? "",
          floorName: data?.floorName ?? "",

          roomId: data?.roomId ?? "",
          roomName: data?.roomName ?? "",

          roomActiveStatus:
            data?.roomActiveStatus !== undefined
              ? data.roomActiveStatus === true ||
                data.roomActiveStatus === "true" ||
                data.roomActiveStatus === 1 ||
                data.roomActiveStatus === "1"
              : true,

          roomSharingType: data?.roomSharingType ?? "",

          totalBookedBeds: bookedBeds,

          isLockerAvailable: lockerAvailable,

          isSnoringRoom: snoringRoom,

          createdBy: data?.createdBy ?? "",
          createdOn: data?.createdOn ?? "",
          updatedOn: data?.updatedOn ?? "",
        });
      } else {
        await Swal.fire({
          icon: "warning",
          title: "Unable to Load",
          text:
            response.data?.message || "Floor and room details were not found.",
        });
      }
    } catch (error) {
      console.error("=================================");
      console.error("GET FLOOR ROOM ERROR");
      console.error(error);
      console.error("=================================");

      if (error.response?.status === 401 || error.response?.status === 403) {
        await Swal.fire({
          icon: "error",
          title: "Session Expired",
          text: "Please login again.",
          confirmButtonText: "Login",
        });

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          "Unable to load floor and room details.",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = async (e) => {
    const { name, value } = e.target;

    // =====================================================
    // ROOM SHARING TYPE VALIDATION
    // =====================================================

    if (name === "roomSharingType") {
      const bookedBeds = Number(
        floorData.totalBookedBeds || totalBedsBooked || 0,
      );

      let selectedSharingCount = 0;

      if (value === "Single Room") {
        selectedSharingCount = 1;
      } else {
        const match = value.match(/\d+/);
        selectedSharingCount = match ? Number(match[0]) : 0;
      }

      console.log("Total Booked Beds:", bookedBeds);

      console.log("Selected Sharing Count:", selectedSharingCount);

      // ===================================================
      // BLOCK INVALID SELECTION
      // ===================================================

      if (bookedBeds > selectedSharingCount) {
        await Swal.fire({
          icon: "error",
          title: "Invalid Sharing Type",
          text: `This room already has ${bookedBeds} booked beds. You cannot select ${selectedSharingCount} sharing.`,
          confirmButtonText: "OK",
        });

        return;
      }

      // ===================================================
      // VALID SELECTION
      // ===================================================

      setFloorData((prev) => ({
        ...prev,
        roomSharingType: value,
      }));

      return;
    }

    // =====================================================
    // OTHER FIELDS
    // =====================================================

    setFloorData((prev) => ({
      ...prev,

      [name]:
        name === "roomActiveStatus" ||
        name === "isLockerAvailable" ||
        name === "isSnoringRoom"
          ? value === "true"
          : value,
    }));
  };

  // =========================================================
  // GET SHARING TYPE COUNT
  // =========================================================

  const getSharingTypeCount = (sharingType) => {
    if (!sharingType) {
      return 0;
    }

    if (sharingType === "Single Room") {
      return 1;
    }

    const match = sharingType.match(/\d+/);

    return match ? Number(match[0]) : 0;
  };

  // =========================================================
  // UPDATE API
  // =========================================================

  const handleUpdateFloor = async () => {
    try {
      // =====================================================
      // BASIC VALIDATION
      // =====================================================

      if (!floorData.floorId || !floorData.roomId) {
        await Swal.fire({
          icon: "warning",
          title: "Missing Details",
          text: "Floor ID or Room ID is missing.",
        });

        return;
      }

      if (!floorData.roomSharingType) {
        await Swal.fire({
          icon: "warning",
          title: "Sharing Type Required",
          text: "Please select room sharing type.",
        });

        return;
      }

      // =====================================================
      // BOOKED BED VALIDATION
      // =====================================================

      const bookedBeds = Number(floorData.totalBookedBeds || 0);

      const selectedSharingTypeCount = getSharingTypeCount(
        floorData.roomSharingType,
      );

      console.log("=================================");
      console.log("ROOM CAPACITY VALIDATION");
      console.log("Total Beds Booked:", bookedBeds);
      console.log("Selected Sharing Type:", floorData.roomSharingType);
      console.log("Selected Sharing Type Count:", selectedSharingTypeCount);
      console.log("=================================");

      if (bookedBeds > selectedSharingTypeCount) {
        await Swal.fire({
          icon: "warning",
          title: "Invalid Sharing Type",
          text: `This room already has ${bookedBeds} booked beds. Please select a sharing type of ${bookedBeds} or higher.`,
          confirmButtonText: "OK",
        });

        return;
      }

      setUpdating(true);

      // =====================================================
      // GET USER
      // =====================================================

      const user = getLoggedUser();

      const token = user?.jwtToken;

      console.log("User before update:", user);

      console.log("JWT Token exists:", !!token);

      if (!token) {
        await Swal.fire({
          icon: "error",
          title: "Session Expired",
          text: "Login token not found. Please login again.",
          confirmButtonText: "Login",
        });

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
        return;
      }

      // =====================================================
      // BOOLEAN VALUES
      // =====================================================

      const roomActiveStatus =
        floorData.roomActiveStatus === true ||
        floorData.roomActiveStatus === "true" ||
        floorData.roomActiveStatus === 1 ||
        floorData.roomActiveStatus === "1";

      const isLockerAvailable =
        floorData.isLockerAvailable === true ||
        floorData.isLockerAvailable === "true" ||
        floorData.isLockerAvailable === 1 ||
        floorData.isLockerAvailable === "1";

      const isSnoringRoom =
        floorData.isSnoringRoom === true ||
        floorData.isSnoringRoom === "true" ||
        floorData.isSnoringRoom === 1 ||
        floorData.isSnoringRoom === "1";

      // =====================================================
      // UPDATE PAYLOAD
      // =====================================================

      const payload = {
        floorId: Number(floorData.floorId),

        roomId: Number(floorData.roomId),

        roomActiveStatus: roomActiveStatus,

        roomSharingType: floorData.roomSharingType,

        isLockerAvailable: isLockerAvailable,

        isSnoringRoom: isSnoringRoom,
      };

      console.log("=================================");
      console.log("UPDATE FLOOR ROOM PAYLOAD");
      console.log(JSON.stringify(payload, null, 2));
      console.log("=================================");

      // =====================================================
      // DYNAMIC PUT API
      // =====================================================

      const updateUrl = `${API.V1}/floor_rooms/updatefloor`;

      console.log("PUT URL:", updateUrl);

      const response = await axios.put(updateUrl, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // =====================================================
      // RESPONSE
      // =====================================================

      console.log("=================================");
      console.log("UPDATE RESPONSE");
      console.log(response.data);
      console.log("=================================");

      if (
        response.status === 200 &&
        (response.data?.statusCode === 200 ||
          response.data?.statusCode === undefined)
      ) {
        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          text:
            response.data?.message ||
            "Floor and room details updated successfully.",
          confirmButtonText: "OK",
        });

        navigate(-1);
      } else {
        await Swal.fire({
          icon: "warning",
          title: "Update Failed",
          text:
            response.data?.message ||
            "Unable to update floor and room details.",
        });
      }
    } catch (error) {
      console.error("=================================");
      console.error("UPDATE FLOOR ROOM ERROR");
      console.error(error);
      console.error("=================================");

      console.error("Response:", error.response?.data);

      console.error("Status:", error.response?.status);

      if (error.response?.status === 401 || error.response?.status === 403) {
        await Swal.fire({
          icon: "error",
          title: "Session Expired",
          text: "Please login again.",
          confirmButtonText: "Login",
        });

        localStorage.clear();
        sessionStorage.clear();

        navigate("/");
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          "Something went wrong while updating.",
      });
    } finally {
      setUpdating(false);
    }
  };

  // =========================================================
  // LOADING
  // =========================================================

  if (loading) {
    return (
      <>
        <style>{styles}</style>

        <div className="update-floor-loading">
          <div className="loading-spinner"></div>

          <span>Loading floor and room details...</span>
        </div>
      </>
    );
  }

  // =========================================================
  // UI
  // =========================================================

  return (
    <>
      <style>{styles}</style>

      <div className="update-floor-page">
        <div className="update-floor-container">
          {/* HEADER */}

          <div className="update-floor-header">
            <div>
              <h2>Update Floor & Room</h2>

              <p>Update room status, sharing type and room facilities</p>
            </div>
          </div>

          {/* BASIC DETAILS */}

          <div className="section-card">
            <div className="section-heading">
              <span className="section-icon">🏢</span>

              <div>
                <h3>Basic Details</h3>

                <p>Floor and room information</p>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Floor Name</label>

                <input type="text" value={floorData.floorName} readOnly />
              </div>

              <div className="form-group">
                <label>Room Name</label>

                <input type="text" value={floorData.roomName} readOnly />
              </div>
            </div>
          </div>

          {/* ROOM SETTINGS */}

          <div className="section-card">
            <div className="section-heading">
              <span className="section-icon">⚙️</span>

              <div>
                <h3>Room Settings</h3>

                <p>Update room configuration</p>
              </div>
            </div>

            <div className="form-grid">
              {/* SHARING TYPE */}

              <div className="form-group">
                <label>
                  Room Sharing Type
                  <span className="required">*</span>
                </label>

                <select
                  name="roomSharingType"
                  value={floorData.roomSharingType}
                  onChange={handleChange}
                >
                  <option value="">Select Sharing Type</option>

                  <option value="Single Room">Single Room</option>

                  <option value="2 Sharing Room">2 Sharing Room</option>

                  <option value="3 Sharing Room">3 Sharing Room</option>

                  <option value="4 Sharing Room">4 Sharing Room</option>

                  <option value="5 Sharing Room">5 Sharing Room</option>

                  <option value="6 Sharing Room">6 Sharing Room</option>
                </select>

                <div className="booked-bed-info">
                  <span>🛏️ Total Beds Booked:</span>

                  <strong>{floorData.totalBookedBeds}</strong>

                  <span>
                    / {getSharingTypeCount(floorData.roomSharingType) || "-"}
                  </span>
                </div>
              </div>

              {/* ACTIVE STATUS */}

              <div className="form-group">
                <label>
                  Room Active Status
                  <span className="required">*</span>
                </label>

                <select
                  name="roomActiveStatus"
                  value={String(floorData.roomActiveStatus)}
                  onChange={handleChange}
                >
                  <option value="true">Active</option>

                  <option value="false">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* ROOM FACILITIES */}

          <div className="section-card">
            <div className="section-heading">
              <span className="section-icon">✨</span>

              <div>
                <h3>Room Facilities</h3>

                <p>Configure additional room options</p>
              </div>
            </div>

            <div className="facility-grid">
              {/* LOCKER */}

              <div
                className={`facility-card ${
                  floorData.isLockerAvailable ? "facility-active" : ""
                }`}
              >
                <div className="facility-content">
                  <span className="facility-icon">🔐</span>

                  <div>
                    <strong>Locker Available</strong>

                    <small>Locker facility in room</small>
                  </div>
                </div>

                <select
                  name="isLockerAvailable"
                  value={String(floorData.isLockerAvailable)}
                  onChange={handleChange}
                >
                  <option value="true">Yes</option>

                  <option value="false">No</option>
                </select>
              </div>

              {/* SNORING ROOM */}

              <div
                className={`facility-card ${
                  floorData.isSnoringRoom ? "facility-active" : ""
                }`}
              >
                <div className="facility-content">
                  <span className="facility-icon">😴</span>

                  <div>
                    <strong>Snoring Room</strong>

                    <small>Room designated for snoring</small>
                  </div>
                </div>

                <select
                  name="isSnoringRoom"
                  value={String(floorData.isSnoringRoom)}
                  onChange={handleChange}
                >
                  <option value="true">Yes</option>

                  <option value="false">No</option>
                </select>
              </div>
            </div>
          </div>

          {/* CURRENT VALUES */}

          <div className="current-values-card">
            <strong>Current Room Values</strong>

            <div className="current-values">
              <span>
                Total Beds Booked:
                <b> {floorData.totalBookedBeds}</b>
              </span>

              <span>
                Sharing Capacity:
                <b> {getSharingTypeCount(floorData.roomSharingType)}</b>
              </span>

              <span>
                Locker Available:
                <b>{floorData.isLockerAvailable ? " YES" : " NO"}</b>
              </span>

              <span>
                Snoring Room:
                <b>{floorData.isSnoringRoom ? " YES" : " NO"}</b>
              </span>
            </div>
          </div>

          {/* INFORMATION */}

          <div className="info-card">
            <div className="info-icon">ℹ️</div>

            <div>
              <strong>Room Information</strong>

              <p>Created by: {floorData.createdBy || "-"}</p>

              <p>Created on: {floorData.createdOn || "-"}</p>

              <p>Last updated: {floorData.updatedOn || "-"}</p>
            </div>
          </div>

          {/* ACTIONS */}

          <div className="update-floor-actions">
            <button
              type="button"
              className="cancel-floor-btn"
              onClick={() => navigate(-1)}
              disabled={updating}
            >
              Cancel
            </button>

            <button
              type="button"
              className="update-floor-btn"
              onClick={handleUpdateFloor}
              disabled={updating}
            >
              {updating ? "Updating..." : "✓ Update Floor & Room"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// =========================================================
// CSS
// =========================================================

const styles = `
* {
  box-sizing: border-box;
}

.update-floor-page {
  min-height: 100vh;
  padding: 40px 20px;
  background: #f5f7fb;
}

.update-floor-container {
  width: 100%;
  max-width: 850px;
  margin: 0 auto;
}

.update-floor-header {
  background: #ffffff;
  border-radius: 18px;
  padding: 28px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.07);
  margin-bottom: 20px;
}

.update-floor-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 27px;
  font-weight: 800;
}

.update-floor-header p {
  margin: 7px 0 0;
  color: #6b7280;
  font-size: 14px;
}

.section-card {
  background: #ffffff;
  border-radius: 18px;
  padding: 27px;
  margin-bottom: 20px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 25px;
  padding-bottom: 17px;
  border-bottom: 1px solid #edf0f4;
}

.section-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #eef2ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.section-heading h3 {
  margin: 0;
  color: #1f2937;
  font-size: 18px;
}

.section-heading p {
  margin: 4px 0 0;
  color: #9ca3af;
  font-size: 13px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  color: #374151;
  font-size: 14px;
  font-weight: 700;
}

.required {
  color: #dc2626;
  margin-left: 4px;
}

.form-group input,
.form-group select {
  width: 100%;
  height: 47px;
  padding: 0 14px;
  border: 1px solid #d7dce3;
  border-radius: 10px;
  background: #ffffff;
  color: #1f2937;
  font-size: 15px;
  outline: none;
  transition: 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #4f46e5;
  box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
}

.form-group input[readonly] {
  background: #f3f4f6;
  color: #6b7280;
  cursor: not-allowed;
}

.form-group select {
  cursor: pointer;
}

.booked-bed-info {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 2px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  color: #64748b;
  font-size: 12px;
}

.booked-bed-info strong {
  color: #4f46e5;
  font-size: 14px;
}

.facility-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.facility-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  background: #fafafa;
  transition: 0.2s ease;
}

.facility-active {
  border-color: #86efac;
  background: #f0fdf4;
}

.facility-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.facility-icon {
  font-size: 25px;
}

.facility-content strong {
  display: block;
  color: #1f2937;
  font-size: 14px;
}

.facility-content small {
  display: block;
  margin-top: 4px;
  color: #9ca3af;
  font-size: 12px;
}

.facility-card select {
  height: 38px;
  padding: 0 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
}

.current-values-card {
  background: #ffffff;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 20px;
  border: 1px solid #e5e7eb;
}

.current-values-card strong {
  display: block;
  margin-bottom: 12px;
  color: #374151;
}

.current-values {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.current-values span {
  font-size: 13px;
  color: #6b7280;
}

.current-values b {
  color: #111827;
}

.info-card {
  display: flex;
  gap: 13px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  padding: 18px;
  margin-bottom: 20px;
  color: #1e40af;
}

.info-icon {
  font-size: 21px;
}

.info-card strong {
  font-size: 14px;
}

.info-card p {
  margin: 4px 0;
  font-size: 12px;
}

.update-floor-actions {
  background: #ffffff;
  border-radius: 18px;
  padding: 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
}

.cancel-floor-btn,
.update-floor-btn {
  height: 47px;
  padding: 0 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s ease;
}

.cancel-floor-btn {
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
}

.cancel-floor-btn:hover {
  background: #f3f4f6;
}

.update-floor-btn {
  min-width: 190px;
  border: none;
  background: #4f46e5;
  color: #ffffff;
}

.update-floor-btn:hover {
  background: #4338ca;
  transform: translateY(-1px);
}

.update-floor-btn:disabled,
.cancel-floor-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.update-floor-loading {
  min-height: 100vh;
  background: #f5f7fb;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #4f46e5;
  font-size: 15px;
  font-weight: 700;
}

.loading-spinner {
  width: 25px;
  height: 25px;
  border: 3px solid #e5e7eb;
  border-top-color: #4f46e5;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 650px) {
  .update-floor-page {
    padding: 20px 12px;
  }

  .update-floor-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .form-grid,
  .facility-grid {
    grid-template-columns: 1fr;
  }

  .section-card {
    padding: 20px;
  }

  .update-floor-actions {
    flex-direction: column-reverse;
  }

  .cancel-floor-btn,
  .update-floor-btn {
    width: 100%;
  }
}
`;

export default UpdateFloorData;
